// Card 58, works in progress (R548, ui-map-interface.md §17.3.58).
//
// A chart notes harbour works under way — "Works in progress (2026)" —
// where the survey found a quay being built; an old sheet wrote "a mole
// a-building". The note here stands at the commander's own hold while its
// build queue is not empty, with the head of the queue and the world
// minutes to its finish. A commander then reads her own works off the map,
// and card 25's notice in the column is not the only place the work is
// stated.
//
// **OWN HOLD ONLY.** A stranger's queue is not on the wire, so there is
// nothing to draw over anyone else's water.
//
// The clock is `state.jobLive`, which anchors the wire's ETA on the last
// Empire frame and counts it down on the world clock. The piece re-labels
// on `'minute'` and on every Empire frame.

import { chart as S } from '../../../gen/strings';
import { crane } from '../../../render/chart-glyphs';
import { toRoman } from '../../../render/roman';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, faceOf, inkOf } from './shared';

export class WorksPiece implements Piece {
  readonly id = 'works';
  readonly tier = 'screen';
  readonly pack = 'static';
  readonly film = 'hide';
  readonly rebuildOn = ['hello', 'state', 'minute', 'reading', 'font'] as const;

  /** The head of the queue and the one behind it, or null. The tests read it. */
  head: { label: string; level: number; etaSec: number } | null = null;
  next: { label: string; level: number } | null = null;

  /** Nothing to note while the queue is empty, and nothing in the
   *  settlement: the season is over and no work of it lands (R2094). */
  visible(ctx: ChartContext): boolean {
    if (ctx.state.settlementEndsMs !== 0) return false;
    const queue = ctx.state.empire?.queue;
    return queue !== undefined && queue.length > 0;
  }

  rebuild(ctx: ChartContext): void {
    this.head = null;
    this.next = null;
    const queue = ctx.state.empire?.queue ?? [];
    if (queue.length === 0) return;
    // The head is the job holding a slot; a queue whose slots are all idle
    // still has a head, and it is the first row a player put in.
    const at = Math.max(0, queue.findIndex((j) => j.active));
    const job = queue[at];
    this.head = { label: job.label, level: job.level, etaSec: ctx.state.jobEtaSec(job) };
    const after = queue[at + 1];
    if (after) this.next = { label: after.label, level: after.level };
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const head = this.head;
    if (!head) return;
    const t = ctx.cfg.pieces.works;
    const hist = ctx.reading === 'historical';
    const r = t[ctx.reading];
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, CHART_INK.pale);
    const x = ctx.scene.home.x + t.fig.crane_dx;
    const y = ctx.scene.home.y + t.fig.crane_dy;
    crane(sinks.segments, x, y, ink, t.fig.alpha, r.width);
    const lx = x + t.fig.label_dx;
    if (hist) {
      const h = t.historical.fig;
      ctx.glyphs.word(
        sinks.quads,
        S.works_historical({ job: romanLevel(head.label, head.level) }),
        lx, y + h.label_dy, face, t.text.main, ink, r.alpha, { anchor: 'right' },
      );
      return;
    }
    const f = t.modern.fig;
    // The two lines are one block, so they grow about one pivot.
    const pivot = { x: lx, y };
    ctx.glyphs.word(
      sinks.quads,
      S.works_modern({
        job: head.label,
        min: Math.ceil(head.etaSec / 60),
      }),
      lx, y + f.label_dy, face, t.text.main, ink, r.alpha, { anchor: 'right', pivot },
    );
    if (!this.next) return;
    ctx.glyphs.word(
      sinks.quads,
      S.works_next({ job: this.next.label }),
      lx, y + f.next_dy, face, t.text.sub,
      inkOf(r.ink, CHART_INK.faint), f.sub_alpha, { anchor: 'right', pivot },
    );
  }
}

/**
 * The wire's label with its trailing level as a Roman numeral (R566).
 *
 * A building job's label is `<Name> <level>` as the server writes it, so
 * the card must not add the level again; the historical reading rewrites
 * the figure it finds at the end, and a label with no level stays as it is.
 */
function romanLevel(label: string, level: number): string {
  const tail = ` ${level}`;
  if (level <= 0 || !label.endsWith(tail)) return label;
  return `${label.slice(0, -tail.length)} ${toRoman(level)}`;
}

/** The registry's entry. */
export const works = new WorksPiece();
